import { Map as MaplibreMap } from 'maplibre-gl';
import { IFlightItem } from '../flights/types';
import { DeckLayerCreator } from './deck-layer-creator';
import { DeckOverlayPlugin } from './deck-overlay-plugin';
import { TagLeaderItem } from './types';

// TODO this could be scaled with the view port
const MAX_DIRECTION_MULTIPLIER = 13;
const MAX_ARRANGE_COUNT = 333; // The 'give-up' threshold
const PLANE_TAG_SPACING = 15;

// TODO this could be scaled with the view port too
// Directions in which we'll try to place the tag
const directions = [
    [20, 20],
    [20, -20],
    [-20, -20],
    [-20, 20],
    [0, 28],
    [28, 0],
    [0, -28],
    [-28, 0],
    [11, -26],
    [26, -11],
    [26, 11],
    [11, 26],
    [-11, 26],
    [-26, 11],
    [-26, -11],
    [-11, -26]
];

interface TagItemPosition {
    x: number;
    y: number;
}

interface Tag {
    left: number;
    right: number;
    top: number;
    bottom: number;
    width: number;
    height: number;
}

interface TagItem {
    flight: IFlightItem;
    tagPosition: TagItemPosition;
    tag: Tag;
}

export class AutoArrange extends DeckLayerCreator {
    constructor(plugin: DeckOverlayPlugin) {
        super(plugin);
    }

    private _version = 0;
    public get version() {
        return this._version;
    }

    arrange(data: IFlightItem[], map: MaplibreMap, getTextBounds: (flight: IFlightItem) => [number, number, number, number]) {
        const toArrange: TagItem[] = [];
        let doneCount = 0;
        const bounds = map.getBounds();

        for (const flight of data) {
            if (!bounds.contains(this.getFlightPosition(flight))) {
                continue;
            }
            const [x, y, width, height] = getTextBounds(flight);
            toArrange.push({
                flight,
                // just use tag.x, tag.y
                tagPosition: { x, y },
                tag: {
                    left: x,
                    right: x + width,
                    top: y,
                    bottom: y + height,
                    width: width,
                    height: height
                }
            });

            if (++doneCount > MAX_ARRANGE_COUNT) {
                break;
            }
        }

        const tagLeaders = [] as TagLeaderItem[];

        for (const item of toArrange) {
            adjustTag(toArrange, item, map._camera.transform.width, map._camera.transform.height);

            const fromPixels: [number, number] = [item.flight.tagOffsetX, item.flight.tagOffsetY];

            // Tags is to the left of plane
            if (item.tag.left < item.tagPosition.x) {
                // Move the leader to the right edge
                fromPixels[0] += item.tag.width - 1;
            }

            // Tag's above the plane
            if (item.tag.bottom < item.tagPosition.y) {
                // Move the leader to the bottom of the tag
                fromPixels[1] += item.tag.height - 1;
            }

            tagLeaders.push({
                offset: fromPixels,
                flight: item.flight
            });
        }


        this._version++;
        return tagLeaders;
    }
}

function adjustTag(items: TagItem[], item: TagItem, width: number, height: number): void {
    // Try each direction with a different multiplier
    for (let mult = 1; mult < MAX_DIRECTION_MULTIPLIER; mult++) {
        for (let az = 0; az < directions.length; az++) {
            let [x, y] = directions[az];

            if (mult === 9) {
                x /= 2;
                y /= 2;
            } else {
                x *= mult;
                y *= mult;
            }

            if (x < 0) {
                x -= item.tag.width;
            }

            if (y < 0) {
                y -= item.tag.height;
            }

            // Try new offset
            item.flight.tagOffsetX = x;
            item.flight.tagOffsetY = y;

            // Build test area
            const testAreaRect = item.tag;

            testAreaRect.left = item.tagPosition.x + item.flight.tagOffsetX;
            testAreaRect.right = item.tagPosition.x + item.flight.tagOffsetX + item.tag.width;
            testAreaRect.top = item.tagPosition.y + item.flight.tagOffsetY;
            testAreaRect.bottom = item.tagPosition.y + item.flight.tagOffsetY + item.tag.height;

            if (testAreaRect.left > testAreaRect.right) {
                const temp = testAreaRect.left;
                testAreaRect.left = testAreaRect.right;
                testAreaRect.right = temp;
            }

            if (testAreaRect.top > testAreaRect.bottom) {
                const temp = testAreaRect.bottom;
                testAreaRect.bottom = testAreaRect.top;
                testAreaRect.top = temp;
            }

            // If off-screen
            if (testAreaRect.left < 0 || testAreaRect.top < 0 || testAreaRect.bottom > height || testAreaRect.right > width) {
                continue;
            }

            let overlapped = false;
            for (const test of items) {
                if (test === item) {
                    continue;
                }

                if (overlaps(testAreaRect, test.tag) || overlapsPointWithSpacing(testAreaRect, test.tagPosition, PLANE_TAG_SPACING)) {
                    overlapped = true;
                    break;
                }
            }

            if (!overlapped) {
                return;
            }
        }
    }
    // Didn't find anything
    item.tag.left = item.tagPosition.x;
    item.tag.right = item.tagPosition.x + item.tag.width;
    item.tag.top = item.tagPosition.y;
    item.tag.bottom = item.tagPosition.y + item.tag.height;
    item.flight.tagOffsetX = 0;
    item.flight.tagOffsetY = 0;
}

function overlapsPointWithSpacing(rectA: Tag, point: TagItemPosition, spacing: number) {
    const left = point.x - spacing;
    const right = point.x + spacing;
    const top = point.y - spacing;
    const bottom = point.y + spacing;

    return rectA.left < right && rectA.right > left && rectA.top < bottom && rectA.bottom > top;
}

function overlaps(rectA: Tag, rectB: Tag) {
    return rectA.left < rectB.right && rectA.right > rectB.left && rectA.top < rectB.bottom && rectA.bottom > rectB.top;
}
